import React from 'react'
import styled from 'styled-components'
import { accents, brand, semantic } from 'sutra/tokens/colors'
import { spacing } from 'sutra/tokens/spacing'
import { typography } from 'sutra/tokens/typography'

export interface BadgeIndicatorProps extends Omit<
  React.HTMLAttributes<HTMLSpanElement>,
  'children'
> {
  // The element the indicator is anchored to; it is rendered untouched.
  children: React.ReactNode
  // A bare dot, for "something changed here" with no number.
  dot?: boolean
  // A count; nothing renders for 0. Above 99 it reads "99+". Wins over `dot`.
  count?: number
  // [x, y] in px, antd's convention: a negative x moves the indicator inward,
  // a positive y moves it down.
  offset?: [number, number]
  // What assistive tech announces for the indicator. Without it a count reads
  // as its bare text and a dot is hidden.
  label?: string
}

const OVERFLOW_COUNT = 99
const DOT_SIZE = 6
const COUNT_HEIGHT = 14

const Root = styled.span`
  position: relative;
  display: inline-block;
  width: fit-content;
  line-height: 1;
`

interface IndicatorProps {
  $offset: [number, number]
}

const Indicator = styled.span<IndicatorProps>`
  position: absolute;
  top: 0;
  right: ${({ $offset }) => -$offset[0]}px;
  margin-top: ${({ $offset }) => $offset[1]}px;
  transform: translate(50%, -50%);
  transform-origin: 100% 0;
  box-sizing: border-box;
  background: ${accents.crimsonBloom[500]};
  box-shadow: 0 0 0 1px ${semantic.bg};
`

const Dot = styled(Indicator)`
  width: ${DOT_SIZE}px;
  height: ${DOT_SIZE}px;
  border-radius: 100%;
`

const Count = styled(Indicator)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: ${COUNT_HEIGHT}px;
  height: ${COUNT_HEIGHT}px;
  padding: 0 ${spacing[2]}px;
  border-radius: ${COUNT_HEIGHT / 2}px;
  color: ${brand.white};
  font-size: ${typography.text.xs.fontSize}px;
  font-weight: ${typography.weights.regular};
  line-height: ${COUNT_HEIGHT}px;
  white-space: nowrap;
`

const countText = (count: number): string =>
  count > OVERFLOW_COUNT ? `${OVERFLOW_COUNT}+` : String(count)

export const BadgeIndicator = React.forwardRef<
  HTMLSpanElement,
  BadgeIndicatorProps
>(({ children, dot, count = 0, offset = [0, 0], label, ...rest }, ref) => {
  // An indicator with a label is a status region: it names itself, and a
  // later change to it is announced.
  const status = label ? ({ role: 'status', 'aria-label': label } as const) : {}
  return (
    <Root ref={ref} {...rest}>
      {children}
      {count > 0 ? (
        <Count $offset={offset} {...status}>
          {countText(count)}
        </Count>
      ) : dot ? (
        <Dot $offset={offset} {...(label ? status : { 'aria-hidden': true })} />
      ) : null}
    </Root>
  )
})

BadgeIndicator.displayName = 'BadgeIndicator'
